import { ShoppingBag, Headphones, Battery, Cable, Smartphone, Package } from 'lucide-react';

const AccessoriesSection = () => {
  const categories = [
    {
      icon: Cable,
      title: 'Chargeurs & Câbles',
      items: ['Chargeurs rapides (GaN / USB-C PD)', 'Câbles Lightning / USB-C', 'Chargeurs secteur & voiture', 'Câbles 3-en-1', 'Adaptateurs USB-C'],
      gradient: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-100',
    },
    {
      icon: Headphones,
      title: 'Audio',
      items: ['Écouteurs filaires', 'Écouteurs Bluetooth', 'Casques audio', 'Enceintes portables'],
      gradient: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      iconBg: 'bg-purple-100',
    },
    {
      icon: Battery,
      title: 'Énergie',
      items: ['Batteries externes (powerbanks)', 'Chargeurs sans fil Qi', 'Chargeurs MagSafe', 'Stations de charge multi-appareils', 'Chargeurs solaires'],
      gradient: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      iconBg: 'bg-green-100',
    },
    {
      icon: Smartphone,
      title: 'Coques & Étuis',
      items: ['Coques silicone', 'Coques rigides antichoc', 'Étuis portefeuille', 'Housses tablette', 'Pochettes & sacoches'],
      gradient: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      iconBg: 'bg-orange-100',
    },
    {
      icon: Package,
      title: 'Supports & Photo',
      items: ['Supports voiture & bureau', 'Trépieds & perches selfie', 'Ring lights', 'Brassards sport', 'Stabilisateurs'],
      gradient: 'from-cyan-500 to-teal-600',
      bgColor: 'bg-cyan-50',
      iconBg: 'bg-cyan-100',
    },
    {
      icon: ShoppingBag,
      title: 'Gadgets & Divers',
      items: ['Adaptateurs & hubs', 'Lecteurs carte SD', 'Clés USB / stockage', 'Nettoyants écran', 'Stylets & accessoires'],
      gradient: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      iconBg: 'bg-amber-100',
    },
  ];

  return (
    <section
      id="accessoires"
      className="section-spacing relative overflow-hidden"
      data-testid="accessories-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-gradient-to-br from-amber-400/10 to-orange-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-full mb-6">
            <ShoppingBag size={16} className="text-slate-600" />
            <span className="text-slate-700 font-semibold text-sm uppercase tracking-wider" data-testid="accessories-label">
              Accessoires
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="accessories-title">
            Tout ce qu'il vous faut,{' '}
            <span className="gradient-text">au même endroit</span>
          </h2>
          <p className="text-lg text-slate-600" data-testid="accessories-description">
            Une large sélection d'accessoires pour vos appareils. 
            Venez découvrir notre gamme en boutique !
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="accessories-grid">
          {categories.map((category, index) => (
            <div
              key={index}
              className="group relative"
              data-testid={`accessory-card-${index}`}
            >
              {/* Card glow on hover */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${category.gradient} rounded-[24px] opacity-0 group-hover:opacity-15 blur-xl transition-opacity duration-500`} />
              
              <div className={`relative h-full ${category.bgColor} rounded-2xl p-6 border border-white/50 hover-lift transition-all`}>
                {/* Icon */}
                <div className={`w-14 h-14 ${category.iconBg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <category.icon size={28} className="text-slate-700" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-slate-900 mb-4">{category.title}</h3>

                {/* Items list */}
                <ul className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center gap-2 text-slate-600 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${category.gradient}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100">
            <div className="text-4xl">🛍️</div>
            <div className="text-left">
              <p className="font-bold text-slate-900 text-lg">Besoin d'un accessoire ?</p>
              <p className="text-slate-600">Passez en boutique ou contactez-nous !</p>
            </div>
            <a
              href="tel:+33983820754"
              className="px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white font-semibold rounded-xl hover:shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              Nous appeler
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccessoriesSection;
